import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Checkbox, FormControlLabel, List, ListItem, ListItemText, Tooltip } from '@mui/material';
import { useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { ActionType, ResourceType } from '../../common/PricePrgClient';
import { RootState } from '../../store';
import {
    getExternalPricelistPermissionsFromSelectedGroup,
    getExternalPricelists
} from '../externalpricelist/redux/externalPricelistSelectors';
import { getSelectedUserGroupId } from './redux/userGroupSelectors';
import { setRecordPermissionForGroupRequest } from './redux/userGroupSlices';
const mapStateToProps = (state: RootState) => ({
    selectedUserGroupId: getSelectedUserGroupId(state),
    extPricelistPermissions: getExternalPricelistPermissionsFromSelectedGroup(state),
    externalPricelists: getExternalPricelists(state)
});

type Props = ReturnType<typeof mapStateToProps>;

const ManageGroupPricelistPermissions = ({
    selectedUserGroupId,
    extPricelistPermissions,
    externalPricelists
}: Props) => {
    const dispatch = useDispatch();

    const extPricelists = useMemo(() => Object.values(externalPricelists), [externalPricelists]);

    if (selectedUserGroupId == null) {
        return null;
    }

    const handlePermissionSet = (extPricelistId: number, actionType: ActionType, hasPermission: boolean) => {
        dispatch(
            setRecordPermissionForGroupRequest({
                recordId: extPricelistId,
                resourceType: ResourceType.ExternalPricelist,
                userGroupId: selectedUserGroupId,
                actionType: actionType,
                hasPermission: hasPermission
            })
        );
    };

    return (
        <Box component="div" sx={{ display: 'flex', flexDirection: 'column', maxWidth: '450px' }}>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={extPricelistPermissions[0]?.canCreate ?? false}
                        onChange={(event, checked) => handlePermissionSet(0, ActionType.Create, checked)}
                        color="primary"
                    />
                }
                label="Can read all and create new external pricelists"
            />

            <Box
                component="div"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    paddingRight: '9px'
                }}
            >
                <Box component="span" sx={{ paddingRight: '18px' }}>
                    <Tooltip title="Permission to view">
                        <VisibilityIcon color="primary" />
                    </Tooltip>
                </Box>
                <Box component="span" sx={{ paddingRight: '18px' }}>
                    <Tooltip title="Permission to write">
                        <CreateIcon color="primary" />
                    </Tooltip>
                </Box>
                <Box component="span">
                    <Tooltip title="Permission to delete">
                        <DeleteIcon color="secondary" />
                    </Tooltip>
                </Box>
            </Box>

            <List>
                {extPricelists.map(epl => {
                    const pricelistPermission = extPricelistPermissions[epl.externalPricelistId];
                    let canRead: boolean;
                    let canWrite: boolean;
                    let canDelete: boolean;
                    if (pricelistPermission != null) {
                        canRead = pricelistPermission.canRead;
                        canWrite = pricelistPermission.canUpdate;
                        canDelete = pricelistPermission.canDelete;
                    } else {
                        canRead = false;
                        canWrite = false;
                        canDelete = false;
                    }

                    return (
                        <ListItem key={epl.externalPricelistId} disableGutters={true}>
                            <ListItemText
                                primary={
                                    <Box
                                        component="div"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            maxHeight: '25px',
                                            justifyContent: 'space-between'
                                        }}
                                    >
                                        <Box component="span">{epl.name}</Box>
                                        <Box component="div">
                                            <Checkbox
                                                checked={canRead}
                                                onChange={(event, checked) =>
                                                    handlePermissionSet(
                                                        epl.externalPricelistId,
                                                        ActionType.Read,
                                                        checked
                                                    )
                                                }
                                                color="primary"
                                            />
                                            <Checkbox
                                                checked={canWrite}
                                                onChange={(event, checked) =>
                                                    handlePermissionSet(
                                                        epl.externalPricelistId,
                                                        ActionType.Update,
                                                        checked
                                                    )
                                                }
                                                color="primary"
                                            />
                                            <Checkbox
                                                checked={canDelete}
                                                onChange={(event, checked) =>
                                                    handlePermissionSet(
                                                        epl.externalPricelistId,
                                                        ActionType.Delete,
                                                        checked
                                                    )
                                                }
                                                color="primary"
                                            />
                                        </Box>
                                    </Box>
                                }
                            />
                        </ListItem>
                    );
                })}
            </List>
        </Box>
    );
};
export default connect(mapStateToProps)(ManageGroupPricelistPermissions);
